<template>
  <section class="search-section p-4 pb-3 mt-4 rounded flex items-center flex-wrap md:flex-nowrap md:p-2 md:pl-4 md:mt-0">
    <filter-items
      v-model:filterState="filterState"
      class="flex flex-1 flex-wrap justify-start items-center md:flex-nowrap md:flex-none"
      :base-filter-state="baseFilterState"
      :filter-layout="filterLayout"
      :filter-item-option="filterItemOption"
      :filter-max-count="filterMaxCount"
      :last-submit-filter-state="lastSubmitFilterState"
      :product-count-query-params="filterQueryParams"
      :show-filter-wrapper-footer-actions="showFilterItemFooterActions"
      trigger-class="md:max-w-163px"
      :trigger-btn-type="triggerBtnType"
      @clear="clear"
      @submit="handleFilterItemSubmit"
      @close="reset"
    >
      <template #divider>
        <div class="divider" role="separator" />
      </template>
    </filter-items>
    <div class="divider" role="separator" />
    <search-bar @search="goToProductListPage" />
  </section>
</template>

<script lang="ts" setup>
import SearchBar from '@/components/home/search-bar.vue'
import FilterItems from '@/components/search/filter/filter-items.vue'

import { FilterTypeEnum, type FilterLayout } from '@/types/filters'
import type { GetProductsReqParams } from '@/types/product'

import { QueryStringKeys } from '@/constants/commons'

import { useAppStore } from '@/store/app'
import type { SearchRecordItem } from '@/types/search'

const appStore = useAppStore()

// filter 相關
const {
  baseFilterState,
  filterItemOption,
  filterLayout,
  filterState,
  filterMaxCount,
  filterStateUnWrap,
  lastSubmitFilterState,
  getFilterString,
  reset,
  clear,
  saveLastSubmitted,
} = useSearchFilter()
const { regionId } = useRegion()
const { handleSearch } = useSearchInput()

const filterQueryParams = computed<GetProductsReqParams>(() => ({
  filter_regionid: regionId.value,
  ...filterStateUnWrap.value,
  filter_tags: nestedArrToString(filterStateUnWrap.value.filter_tags),
}))
const triggerBtnType = computed(() => (appStore.isMobileScreen ? 'secondary' : 'default'))

useFilters({ asyncDataOpts: { lazy: true, server: false } })

function showFilterItemFooterActions(name: FilterLayout['name']) {
  return appStore.isMobileScreen || !([FilterTypeEnum.LIMIT] as string[]).includes(name)
}

function handleFilterItemSubmit() {
  saveLastSubmitted()
}

function goToProductListPage(item: SearchRecordItem) {
  handleSearch(item, { otherQuery: { [QueryStringKeys.SEARCH_FILTER]: getFilterString() } })
}
</script>

<style lang="scss" scoped>
.search-section {
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.2);

  @screen md {
    box-shadow:
      0px 6px 16px -8px rgba(0, 0, 0, 0.16),
      0px 9px 28px 0px rgba(0, 0, 0, 0.1);
  }

  /* PD: 因這邊樣式希望跟 input 一樣，故需要額外客製化 */
  :deep(.filter-items) {
    @apply gap-2 md:gap-0;

    .btn {
      @apply flex-none py-2 justify-start ring-gray-40;

      &:nth-of-type(1),
      &:nth-of-type(2) {
        @apply w-[calc(50%-5px)] md:w-auto;
      }
      &:nth-of-type(1) {
        @apply mr-1px md:m-0;
      }
      &:nth-of-type(2) {
        @apply ml-1px md:m-0;
      }
      &:nth-of-type(3) {
        @apply flex-1 md:flex-none;
      }
    }

    .etg-btn-text {
      @apply whitespace-normal line-clamp-1 text-left;
    }
  }
}

.divider {
  @apply hidden w-1px h-10 bg-gray-40 mx-4 md:block;
}
</style>
